import React from 'react'
// components
import Tooltip from '@mui/material/Tooltip'
import ControlButtons from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/ControlButtons'
import AiControlButton from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/AiControlButton'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// views
import { Label, LabelContainer, TableHeadLabelWrapper } from './views'
// icons
import { AsteriskIcon } from 'components/FormElements/components/InputLabel'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// constants
import { Modals } from 'constants/modals'
// types
import { IField } from 'types/graphql'
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd'

interface Props {
  label: string
  field?: IField
  sticky?: boolean
  dragHandleProps?: DraggableProvidedDragHandleProps
}

const TableHeadLabel: React.FC<Props> = ({ label, field, sticky = false, dragHandleProps }) => {

  const { isRisk, isRiskTemplate, riskTemplate, deleteForm, deleteField } = React.useContext(RiskTemplateAPIContext)

  const { showModal: showDeleteModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_RISK_MODAL })

  const wrapperRef = React.useRef<HTMLDivElement>(null)

  React.useLayoutEffect(() => {
    if (!sticky) {
      return
    }

    const el = wrapperRef.current
    if (!el) return

    const parent = el.parentElement

    if (!parent) return

    // The vertical scroll container (Wrapper in RiskTemplates/Edit and Risks/Edit) is marked explicitly.
    // Detecting it by overflow size is unreliable: on mount the content may not overflow yet
    // (rows still loading, taller viewport), so the header would never become sticky.
    const scrollContainer = el.closest<HTMLElement>('[data-rt-scroll]')

    if (!scrollContainer) return

    // Find the direct child of scrollContainer that contains el.
    // When sibling tables grow they shift el's position — observing this wrapper
    // lets us recompute rangeStart whenever any content above el changes height.
    let contentWrapper: HTMLElement | null = el.parentElement
    while (contentWrapper && contentWrapper.parentElement !== scrollContainer) {
      contentWrapper = contentWrapper.parentElement
    }

    const supportsScrollTimeline = CSS.supports('animation-timeline: scroll()')

    // Mutable state shared between recompute and updateTransform closures.
    let rangeStart = 0
    let maxTranslate = 0

    // JS fallback for browsers that don't support CSS scroll-driven animations (Firefox < 128).
    const updateTransform = () => {
      const translateY = Math.max(0, Math.min(scrollContainer!.scrollTop - rangeStart, maxTranslate))
      el.style.transform = `translateY(${translateY}px)`
    }

    const recompute = () => {
      // getComputedStyle returns the current animated/JS transform value. Subtracting it from
      // getBoundingClientRect gives the element's natural, un-transformed position — safe to
      // call from ResizeObserver regardless of current scroll or animation state.
      let currentTY = 0
      const transformStr = window.getComputedStyle(el).transform

      if (transformStr && transformStr !== 'none') {
        const m = transformStr.match(/matrix\(([^)]+)\)/)

        if (m) {
          currentTY = parseFloat(m[1].split(',')[5] ?? '0')
        }
      }

      const elRect = el.getBoundingClientRect()
      const containerRect = scrollContainer!.getBoundingClientRect()
      rangeStart = elRect.top - currentTY - containerRect.top + scrollContainer!.scrollTop
      maxTranslate = parent.offsetHeight - el.offsetHeight

      el.style.setProperty('--range-start', `${rangeStart}px`)
      el.style.setProperty('--range-end', `${rangeStart + maxTranslate}px`)
      el.style.setProperty('--max-translate', `${maxTranslate}px`)

      if (!supportsScrollTimeline) {
        updateTransform()
      }
    }

    recompute()

    const observer = new ResizeObserver(recompute)
    // Watch this column's own height (rows added/removed).
    observer.observe(parent)
    // Watch the shared content wrapper: fires when a sibling table grows and shifts our position.
    if (contentWrapper && contentWrapper !== parent) {
      observer.observe(contentWrapper)
    }

    if (!supportsScrollTimeline) {
      scrollContainer.addEventListener('scroll', updateTransform, { passive: true })
    }

    return () => {
      observer.disconnect()
      if (!supportsScrollTimeline) {
        scrollContainer!.removeEventListener('scroll', updateTransform)
      }
    }
  }, [])

  const fieldLink = riskTemplate?.fieldLinks?.find(
    ({ field: { id }, parentFieldId }) => id === field?.id && parentFieldId === field?.parentFieldId
  )

  const onDeleteClick = () => {
    showDeleteModal({
      width: 'sm',
      entity: fieldLink ? 'form' : 'field',
      handleConfirm: () => (fieldLink ? deleteForm(fieldLink) : deleteField(field))
    })
  }

  return (
    <TableHeadLabelWrapper ref={wrapperRef} {...dragHandleProps}>
      <Tooltip title={label} placement='top-start'>
        <LabelContainer>
          {field?.required && <AsteriskIcon />}
          <Label>{label}</Label>
        </LabelContainer>
      </Tooltip>
      {isRiskTemplate && <ControlButtons field={field} onDeleteClick={onDeleteClick} />}
      {riskTemplate.aiEnabled && isRisk && !fieldLink && (
        <AiControlButton isTable field={field} />
      )}
    </TableHeadLabelWrapper>
  )
}

export default TableHeadLabel
